/**
 * Explanatory copy for a campaign condition step's timing (PREC-3908).
 *
 * A condition node holds a lead between two steps until something happens — a LinkedIn invite is
 * accepted, an email is opened, a link is clicked. Users could see *how long* it waits but not
 * what the clock is measured from, nor what happens when the event arrives early, so the copy
 * lives here next to the rules it describes rather than inline in the node.
 *
 * Kept in sync with the backend by hand: `resolveScheduleAddDays`
 * (apps/api/src/schedule/schedule-add-days.util.ts) times the wait, and `InviteGateService`
 * (apps/api/src/schedule/invite-gate.service.ts) resolves the invite gate.
 */

/**
 * The fixed window a "Wait until" invite gate allows before giving up.
 *
 * Mirrors `LINKEDIN_INVITE_WAIT_DAYS` in apps/api/src/schedule/constants.ts. A wait-until node
 * carries no `data.days` of its own, so this is the only number that describes it.
 */
export const LINKEDIN_INVITE_WAIT_DAYS = 30;

/**
 * Copy for the "Wait until" mode — the LinkedIn invite-acceptance gate.
 *
 * Early acceptance is handled by the acceptance webhook, which releases the parked follow-ups
 * immediately and re-times each to now + its own configured offset; a gate that times out
 * withdraws the invite, cancels the follow-ups and ends the campaign for that lead.
 */
export const waitUntilConditionInfo = (): string =>
	`Waits until the invite is accepted, for up to ${LINKEDIN_INVITE_WAIT_DAYS} days. ` +
	"The countdown starts when this step is queued — right after the previous step ran, not when " +
	"the lead was added. " +
	"If the invite is accepted earlier, the following steps are released straight away, each " +
	"keeping its own configured delay — they do not wait out the remaining days. " +
	`If it is still unanswered after ${LINKEDIN_INVITE_WAIT_DAYS} days, the invite is withdrawn, ` +
	"the remaining steps are cancelled and the campaign ends for this lead.";

/**
 * Copy for the "Within X days" mode — a deadline the event has to fall inside.
 *
 * Same early-fulfilment rule as the invite gate: the event arriving clears the schedule's
 * `scheduledFor`, so the next step runs at the next sending window instead of waiting out the
 * window.
 */
export const withinDaysConditionInfo = (): string =>
	"The number of days in which the event must occur, counted from when this step is queued — " +
	"right after the previous step ran, not from when the lead was added. " +
	"If the event happens earlier, the following steps are released straight away and do not wait " +
	"out the remaining days. " +
	"If it has not happened by the deadline, the campaign continues down the No branch. " +
	"Set 0 to run this step at the earliest 10 minutes after the previous one.";

/** The right copy for whichever mode the node is in. */
export const conditionTimingInfo = (waitUntil: boolean | null | undefined): string =>
	waitUntil ? waitUntilConditionInfo() : withinDaysConditionInfo();
